import { HttpClient, HttpContext, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { environment } from 'src/environments/environment.prod';
import { CoreService } from '../services/core.service';
import { createPatchRequestData } from '../utils/patch-body';

import { ViewParamPipe } from '@core/pipes/view-param.pipe';
import { Store } from '@ngrx/store';
import { SystemState } from '@store/system/system-state.model';
import { systemLoadingAction } from '@store/system/system.actions';
import { TableSettings } from '../interfaces/table-settings.model';
import { removeNullOrUndefinedValues } from '../utils/remove-null-values';

interface RequestOptions {
    headers?: HttpHeaders | { [p: string]: string | string[] };
    context?: HttpContext;
    observe?: 'body';
    params?: HttpParams | { [p: string]: string | number | boolean | readonly (string | number | boolean)[] };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
    transferCache?: { includeHeaders?: string[] } | boolean;
}

export abstract class AbstractApiService {
    public apiUrl = '';
    protected http: HttpClient;
    protected coreService: CoreService;
    protected store: Store<SystemState>;
    private viewParam: ViewParamPipe = new ViewParamPipe();
    private fullUrl = (): string => {
        return environment.apiURL + `/${environment.prefix}`;
    };

    protected constructor(
        protected injector: Injector,
        private url = '',
    ) {
        this.store = injector.get(Store);
        this.http = injector.get(HttpClient);
        this.coreService = injector.get(CoreService);
        this.apiUrl = url;
    }

    public getUrl(url: string): string {
        return `${this.fullUrl()}/${url}`;
    }

    public request<T>(req: Observable<T>): Observable<T> {
        this.store.dispatch(systemLoadingAction({ loading: true }));

        return req.pipe(
            tap(() => {
                this.store.dispatch(systemLoadingAction({ loading: false }));
            }),
            catchError((err) => {
                this.store.dispatch(systemLoadingAction({ loading: false }));

                return throwError(() => err);
            }),
        );
    }

    public reqPatch<T>(body: object, url: string): Observable<T> {
        return this.request(this.http.patch<T>(this.getUrl(url), createPatchRequestData(body)));
    }

    public reqPut<T>(body: object, url: string): Observable<T> {
        return this.request(this.http.put<T>(this.getUrl(url), body));
    }

    public reqPost<T>(
        body: object,
        url: string,
        options?: {
            headers?:
                | HttpHeaders
                | {
                      [header: string]: string | string[];
                  };
            params?:
                | HttpParams
                | {
                      [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
                  };
        },
    ): Observable<T> {
        return this.request(
            this.http.post<T>(this.getUrl(url), body, {
                ...options,
            }),
        );
    }

    public reqDelete<T>(url: string, body?: object): Observable<T> {
        return this.request(
            this.http.delete<T>(this.getUrl(url), {
                body,
            }),
        );
    }

    public reqGet<T>(
        params: TableSettings<string | boolean | string[]>,
        url: string,
        headers?: HttpHeaders | { [p: string]: string | string[] },
    ): Observable<T> {
        let resultParams: TableSettings<string | boolean | string[]> = params;
        if (!!params && !!params['filters'] && Array.isArray(params['filters'])) {
            const filterParams = params['filters'].reduce((a, v) => {
                if (v.value !== '' && v.value !== null && v.value !== undefined && !Array.isArray(v.value)) {
                    if (typeof v.value === 'object' && v['filterResultValue']) {
                        return {
                            ...a,
                            [v.key]: this.viewParam.transform(v.value, v['filterResultValue']) || v.value?.id || v.value,
                        };
                    }

                    return {
                        ...a,
                        [v.key]: typeof v.value === 'object' && v.value?.id ? v.value?.id : v.value,
                    };
                } else if (Array.isArray(v.value) && v.value.length) {
                    let resultValue: any[] | string = v.value
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        .filter((item: any) => {
                            if (typeof item === 'object') {
                                return !!item.value;
                            }

                            return item !== undefined && item !== null && item !== '';
                        })
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        .map((result: any) => {
                            if (typeof result === 'object') {
                                return result?.id;
                            }

                            return result;
                        });
                    if (v?.transformToString) {
                        resultValue = (resultValue as any[]).join(',');
                    }

                    return {
                        ...a,
                        [`${v.key}`]: resultValue,
                    };
                }

                return { ...a };
            }, {});

            resultParams = {
                ...params,
                ...filterParams,
            };
            delete resultParams['filters'];
        }

        const options: RequestOptions = {};

        if (resultParams) {
            options.params = resultParams ? removeNullOrUndefinedValues(resultParams) : resultParams;

            return this.request(this.http.get<T>(this.getUrl(url), options));
        }

        return this.request(this.http.get<T>(this.getUrl(url), options));
    }

    public reqGetFile(params: TableSettings<any> | null, url: string): Observable<HttpResponse<Blob>> {
        let resultParams: TableSettings<string | boolean | string[]> | null = params;
        if (!!params && params['filters']) {
            const filterParams = params['filters'].reduce((a, v) => {
                if (v.value !== '' && v.value !== null && v.value !== undefined && !Array.isArray(v.value)) {
                    return {
                        ...a,
                        [v.key]: typeof v.value === 'object' && v.value?.id ? v.value?.id : v.value,
                    };
                } else if (Array.isArray(v.value) && v.value.length) {
                    return {
                        ...a,
                        [`${v.key}`]: v.value.filter((item) => item.value).map((result) => result.id),
                    };
                }

                return { ...a };
            }, {});
            resultParams = {
                ...params,
                ...filterParams,
            };
            delete resultParams['filters'];
        }

        return this.request(
            this.http.get(this.getUrl(url), {
                params: resultParams as any,
                responseType: 'blob',
                observe: 'response',
            }),
        );
    }

    public downloadFile(response: Blob, filename: string): void {
        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(response);
        downloadLink.setAttribute('download', filename);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
    }

    public getFilename(headers: HttpHeaders): string {
        const contentDisposition = headers.get('content-disposition');
        let filename = '';

        if (contentDisposition) {
            const part = contentDisposition.split(';').find((item) => item && item.trim().startsWith('filename'));
            if (part) {
                filename = part
                    .split('=')[1]
                    .trim()
                    .replace(/^"(.+?)"$/, '$1');
            }
        }

        return filename;
    }
}
